import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { EGender } from '@enums/Gender';
import BigNumber from 'bignumber.js';

import { CorrecaoDoseFuncaoRenalContext } from './context';
import type { IDrugInfo } from './IUseCorrecaoDoseFuncaoRenal';

interface IDrugOption {
  drugId: number | null;
  info: IDrugInfo[];
  label: string;
  value: string;
}

const drugs: IDrugOption[] = [
  {
    label: 'Amoxicilina',
    value: 'Amoxicilina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 100,
            text: '- ClCr > 30 mL/min: Nenhum ajuste de dose necessário.',
          },
          {
            id: 101,
            text: '- ClCr 10-30 mL/min: 500 mg, 2 vezes ao dia.',
          },
          {
            id: 102,
            text: '- ClCr < 10 mL/min: 500 mg, 1 vez ao dia.',
          },
        ],
      },
      {
        id: 2,
        title: 'Diálise peritoneal:',
        items: [
          {
            id: 201,
            text: '- A posologia indicada é a mesma na insuficiência renal grave (ClCr < 10mL/min): 500 mg, 1 vez ao dia.',
          },
          {
            id: 202,
            text: '- A amoxicilina não é removida por diálise peritoneal.',
          },
        ],
      },
      {
        id: 3,
        title: 'Hemodiálise:',
        items: [
          {
            id: 301,
            text: '- A posologia indicada é a mesma na insuficiência renal grave (ClCr < 10mL/min): 500 mg, 1 vez ao dia.',
          },
          {
            id: 302,
            text: '- A amoxicilina é removida da circulação por hemodiálise. Portanto, uma dose adicional (500mg para adultos/crianças acima de 40kg e 15mg/kg para crianças abaixo de 40kg) pode ser administrada durante e ao final de cada diálise.',
          },
        ],
      },
      {
        id: 4,
        title: 'Insuficiência Hepática:',
        items: [
          {
            id: 401,
            text: '- Usar com cautela, com monitoramento das funções hepáticas. Não há recomendação específica sobre ajuste de dose.',
          },
        ],
      },
      {
        id: 5,
        title: 'Referências:',
        items: [
          {
            id: 501,
            text: '- Bula do profissional de saúde. Amoxicilina. Brainfarma Indústria Química e Farmacêutica S.A. Aprovada pela Anvisa em 19/12/2024.',
          },
          {
            id: 502,
            text: '- Bula do profissional de saúde. Amoxicilina. GERMED FARMACÊUTICA LTDA. Aprovada pela Anvisa em 21/11/2024.',
          },
          {
            id: 503,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 504,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 17,
  },
  {
    label: 'Aciclovir',
    value: 'Aciclovir',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 90 mL/min: 5-12,4 mg/kg q 8h',
          },
          {
            id: 102,
            text: '- ClCr 90-50 mL/min: sem ajuste',
          },
          {
            id: 103,
            text: '- ClCr 50-10 mL/min: 100% q 12-24h',
          },
          {
            id: 104,
            text: '- ClCr < 10 mL/min: 50% q 24h',
          },
          {
            id: 105,
            text: '- Hemodiálise: dose após sessão',
          },
          {
            id: 106,
            text: '- Diálise Peritonial Ambulatorial Contínua: Dose para ClCr<10',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências',
        items: [
          {
            id: 201,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 2,
  },
  {
    label: 'Amicacina',
    value: 'Amicacina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 90 mL/min: 7,5 mg/kg q 12h ou 15 mg/kg q 24h',
          },
          {
            id: 102,
            text: '- ClCr 90-50 mL/min: 7,5 mg/kg q 12h ou 15 mg/kg q 24h',
          },
          {
            id: 103,
            text: '- ClCr 50-10 mL/min: 7,5 mg/kg q 24h',
          },
          {
            id: 104,
            text: '- ClCr < 10 mL/min: 7,5 mg/kg q 48h',
          },
          {
            id: 105,
            text: '- Hemodiálise: 1/2 da dose normal',
          },
          {
            id: 106,
            text: '- Diálise Peritonial Ambulatorial Contínua: 15-10 mg/litro de dialisado perdido por dia',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 13,
  },
  {
    label: 'Amoxicilina-clavulanato',
    value: 'Amoxicilina-clavulanato',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal - intravenoso:',
        items: [
          {
            id: 101,
            text: '- ClCr > 30 mL/min: Nenhum ajuste de dose necessário.',
          },
          {
            id: 102,
            text: '- ClCr 10-30 mL/min: 1,2 g (1 g + 200 mg) IV seguidos de 600 mg (500 mg + 100 mg) IV a cada 12h.',
          },
          {
            id: 103,
            text: '- ClCr < 10 mL/min: 1,2 g (1 g + 200 mg) IV seguidos de 600 mg (500 mg + 100 mg) IV a cada 24 horas.',
          },
        ],
      },
      {
        id: 2,
        title: 'Insuficiência Renal - via oral:',
        items: [
          {
            id: 201,
            text: '- ClCr > 30 mL/min: Nenhum ajuste de dose necessário.',
          },
          {
            id: 202,
            text: '- ClCr 10-30 mL/min: 250-500 mg, VO a cada 12 horas.',
          },
          {
            id: 203,
            text: '- ClCr < 10 mL/min: 250-500 mg, VO a cada 24 horas.',
          },
        ],
      },
      {
        id: 3,
        title: 'Insuficiência Hepática:',
        items: [
          {
            id: 301,
            text: '- Usar com cautela. Monitoramento das funções hepáticas é recomendado.',
          },
        ],
      },
      {
        id: 4,
        title: 'Referências:',
        items: [
          {
            id: 401,
            text: '- Bula do profissional de saúde. Clavulin® Injetável. GlaxoSmithKline Brasil Ltda,  aprovada pela Anvisa em 24/08/2019.',
          },
          {
            id: 402,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 403,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
          {
            id: 404,
            text: '- U.S. Food & Drug Administration (FDA). FDA Label: Amoxicillin and clavulanate. NuCare Pharmaceuticals,Inc. Revised: 2/2025.',
          },
        ],
      },
    ],
    drugId: 18,
  },
  {
    label: 'Ampicilina',
    value: 'Ampicilina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 90 mL/min: 250-500 mg q 6h',
          },
          {
            id: 102,
            text: '- ClCr 90-50 mL/min: 250-500 mg q 6h',
          },
          {
            id: 103,
            text: '- ClCr 50-10 mL/min: 250-500 mg q 6-12h',
          },
          {
            id: 104,
            text: '- ClCr < 10 mL/min: 250-500 mg q 12-24h',
          },
          {
            id: 105,
            text: '- Diálise Peritonial Ambulatorial Contínua: 250 mg q 12h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 19,
  },
  {
    label: 'Ampicilina-sulbactam',
    value: 'Ampicilina-sulbactam',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 90 mL/min: 2/1 g q 6h',
          },
          {
            id: 102,
            text: '- ClCr 90-50 mL/min: 2/1 g q 6h',
          },
          {
            id: 103,
            text: '- ClCr 50-10 mL/min: 2/1 g q 8-12h',
          },
          {
            id: 104,
            text: '- ClCr < 10 mL/min: 2/1 g q 24h',
          },
          {
            id: 105,
            text: '- Hemodiálise: Dose antes da diálise.',
          },
          {
            id: 106,
            text: '- Diálise Peritonial Ambulatorial Contínua: 2/1 g q 24h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 20,
  },
  {
    label: 'Anfotericina B complexo lipídico',
    value: 'Anfotericina B complexo lipídico',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- A anfotericina B pode causar nefrotoxicidade, sendo necessário ajuste de dose e monitoramento da função renal durante o tratamento.',
          },
          {
            id: 102,
            text: '- Pode-se considerar uma redução de 50% da dose ou administração em dias alternados.',
          },
          {
            id: 103,
            text: '- A hidratação com solução salina antes da infusão pode ajudar a mitigar a toxicidade renal.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Bula do profissional de Saúde. Anforicin B®. Cristália Produtos Químicos Farmacêuticos Ltda​.',
          },
          {
            id: 202,
            text: '- U.S. Food & Drug Administration (FDA). Medication Guides: Amphotericin. Available at https://dps.fda.gov/medguide.',
          },
          {
            id: 203,
            text: '- U.S. Food & Drug Administration (FDA). FDA Label: Amphotericin. Available at https://nctr-crs.fda.gov/fdalabel/ui/search.',
          },
        ],
      },
    ],
    drugId: 21,
  },
  {
    label: 'Anfotericina B desoxicolato',
    value: 'Anfotericina B desoxicolato',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- Usar com cautela, monitorando regularmente os níveis de creatinina e eletrólitos séricos (particularmente potássio e magnésio). Abelcet® é considerado menos nefrotóxico do que a anfotericina B convencional.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Bula do profissional de Saúde. Abelcet® (Complexo Lipídico de Anfotericina B). Teva Farmacêutica Ltda​.',
          },
          {
            id: 202,
            text: '- AmBisome. Label via DailyMed. Food and Drug Administration.',
          },
        ],
      },
    ],
    drugId: 22,
  },
  {
    label: 'Anfotericina B lipossomal',
    value: 'Anfotericina B lipossomal',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- A anfotericina B lipossomal é menos nefrotóxica que a anfotericina B convencional. Não é necessário ajuste de dose em pacientes com insuficiência renal leve a moderada, mas é recomendado monitoramento regular da função renal.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Bula do profissional de Saúde. AmBisome®. United Medical​.',
          },
          {
            id: 202,
            text: '- U.S. Food & Drug Administration (FDA). FDA Label: Amphotericin B. Available at https://nctr-crs.fda.gov/fdalabel/ui/search.',
          },
        ],
      },
    ],
    drugId: 23,
  },
  {
    label: 'Anidulafungina ',
    value: 'Anidulafungina ',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 90 mL/min: 100-200 mg de ataque, seguido de 50-100 mg q 24h',
          },
          {
            id: 102,
            text: '- ClCr 90-50 mL/min: sem ajuste.',
          },
          {
            id: 103,
            text: '- ClCr 50-10 mL/min: sem ajuste.',
          },
          {
            id: 104,
            text: '- ClCr < 10 mL/min: sem ajuste.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Apixabana',
    value: 'Apixabana',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 30 mL/min: Nenhum ajuste de dose é necessário (exceto se paciente com dois ou mais dos seguintes critérios: Idade ≥ 80 anos; Peso ≤ 60 kg; Creatinina ≥ 1,5 mg/d - devem utilizar 2,5 mg, VO, 2 vezes ao dia).',
          },
          {
            id: 102,
            text: '- ClCr 15-29 mL/min: devem usar a medicação com cautela, e a dose deve ser ajustada de acordo com as características individuais do paciente.',
          },
          {
            id: 103,
            text: '- ClCr < 15 mL/min: não é recomendada devido à experiência clínica limitada.',
          },
          {
            id: 104,
            text: '- Diálise: não recomendado devido à falta de dados clínicos.',
          },
        ],
      },
      {
        id: 2,
        title: 'Observações:',
        items: [
          {
            id: 201,
            text: '- Dose padrão: 5 mg, VO, 2 vezes ao dia.',
          },
          {
            id: 202,
            text: '- Pacientes com *2 ou mais dos seguintes critérios* devem utilizar 2,5 mg, VO, 2 vezes ao dia: Idade ≥ 80 anos; Peso ≤ 60 kg; Creatinina ≥ 1,5 mg/dL.',
          },
        ],
      },
      {
        id: 3,
        title: 'Referências:',
        items: [
          {
            id: 301,
            text: '- Bula do profissional de Saúde. Eliquis®. Pfizer Brasil Ltda. 18/09/2024.',
          },
          {
            id: 302,
            text: '- Bula do profissional de Saúde. Apixabana®. Bristol-Myers Squibb Farmacêutica.',
          },
          {
            id: 303,
            text: '- U.S. Food & Drug Administration (FDA). FDA Label: Apixaban. Aphena Pharma Solutions - Tennessee, LLC, Revised: 2/2025.',
          },
        ],
      },
    ],
    drugId: 25,
  },
  {
    label: 'Azitromicina ',
    value: 'Azitromicina ',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 10 mL/min: 500 mg q 24h',
          },
          {
            id: 102,
            text: '- ClCr < 10 mL/min: Usar com cautela devido ao aumento de 33% na exposição sistêmica.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
          {
            id: 203,
            text: '- Bula do profissional de Saúde. Azitromicina®. Eurofarma.',
          },
          {
            id: 204,
            text: '- Bula do Profissional de Saúde Azitromicina Sandoz do Brasil Ind. Farm. Ltda.',
          },
        ],
      },
    ],
    drugId: 31,
  },
  {
    label: 'Caspofungina',
    value: 'Caspofungina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 90 mL/min: 70 mg ataque, 50 mg q 24h',
          },
          {
            id: 102,
            text: '- ClCr 90-50 mL/min: 100%',
          },
          {
            id: 103,
            text: '- ClCr 50-10 mL/min: 100%',
          },
          {
            id: 104,
            text: '- ClCr < 10 mL/min: 100%',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Cefazolina',
    value: 'Cefazolina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 90 mL/min: 1-2 g q 8h',
          },
          {
            id: 102,
            text: '- ClCr 90-50 mL/min: 1-2 g q 8h',
          },
          {
            id: 103,
            text: '- ClCr 50-10 mL/min: 1-2 g q 12h',
          },
          {
            id: 104,
            text: '- ClCr < 10 mL/min: 1-2 g q 24h',
          },
          {
            id: 105,
            text: '- Hemodiálise: Extra 0,5-1 g pós diálise',
          },
          {
            id: 106,
            text: '- Diálise Peritonial Ambulatorial Contínua: 500 mg q 12h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Cefepime',
    value: 'Cefepime',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 90 mL/min: 2 g q 8h',
          },
          {
            id: 102,
            text: '- ClCr 90-50 mL/min: 2 g q 8h',
          },
          {
            id: 103,
            text: '- ClCr 50-10 mL/min: 2 g q 12-24h',
          },
          {
            id: 104,
            text: '- ClCr < 10 mL/min: 1 g q 24h',
          },
          {
            id: 105,
            text: '- Hemodiálise: Extra 1 g pós diálise',
          },
          {
            id: 106,
            text: '- Diálise Peritonial Ambulatorial Contínua: 1-2 g q 48h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 1,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 2,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 49,
  },
  {
    label: 'Cefotaxime',
    value: 'Cefotaxime',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal:',
        items: [
          {
            id: 101,
            text: '- ClCr > 90 mL/min: 2 g q 8h',
          },
          {
            id: 102,
            text: '- ClCr 90-50 mL/min: 2 g q 8-12h',
          },
          {
            id: 103,
            text: '- ClCr 50-10 mL/min: 2 g q 12-24h',
          },
          {
            id: 104,
            text: '- ClCr < 10 mL/min: 2 g q 24-48h',
          },
          {
            id: 105,
            text: '- Hemodiálise: Extra 1 g pós diálise',
          },
          {
            id: 106,
            text: '- Diálise Peritonial Ambulatorial Contínua: 0,5 a 1 g q 24h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: '- Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: '- Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Ceftazidime',
    value: 'Ceftazidime',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 2 g q 8h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 2 g q 8-12h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 2 g q 12-24h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 2 g q 24-48h',
          },
          {
            id: 105,
            text: 'Hemodiálise: Extra 1 g pós diálise',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: 500 mg q 24h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Ceftriaxona',
    value: 'Ceftriaxona',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'Não há necessidade de ajuste de dose.',
          },
          {
            id: 102,
            text: 'Posologia conforme indicação: 1g q 12h (dose padrão) ou 4g/dia (meningite).',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 52,
  },
  {
    label: 'Cefuroxima',
    value: 'Cefuroxima',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 0,75-1,5 g q 8h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 0,75-1,5 g q 8h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 0,75-1,5 g q 8-12h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 0,75-1,5 g q 24h',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose pós diálise',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr<10',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 53,
  },
  {
    label: 'Ciprofloxacino',
    value: 'Ciprofloxacino',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal - via oral',
        items: [
          {
            id: 101,
            text: 'ClCr > 50 mL/min: sem ajuste de dose necessário.',
          },
          {
            id: 102,
            text: 'ClCr 30-50 mL/min: 250-500 mg q 12h',
          },
          {
            id: 103,
            text: 'ClCr 5-29 mL/min: 250-500 mg q 18h',
          },
          {
            id: 104,
            text: 'Pacientes em hemodiálise ou diálise peritoneal devem receber 250-500 mg a cada 24 horas, após a diálise.',
          },
        ],
      },
      {
        id: 2,
        title: 'Insuficiência Renal - intravenoso',
        items: [
          {
            id: 201,
            text: 'ClCr > 30 mL/min: sem ajuste de dose necessário.',
          },
          {
            id: 202,
            text: 'ClCr 5-29 mL/min: 200-400 mg q 18-24 horas',
          },
          {
            id: 203,
            text: 'Hemodiálise: 200 mg q 12-24 horas',
          },
          {
            id: 204,
            text: 'Diálise Peritonial Ambulatorial Contínua: 200 mg q 8 horas',
          },
        ],
      },
      {
        id: 3,
        title: 'Referências:',
        items: [
          {
            id: 301,
            text: 'U.S. Food & Drug Administration (FDA). FDA Label: Ciprofloxacin. Major Pharmaceuticals, Revised: 2/2025.',
          },
          {
            id: 302,
            text: 'U.S. Food & Drug Administration (FDA). FDA Label: Ciprofloxacin. Sagent Pharmaceuticals, Revised: 9/2024.',
          },
          {
            id: 303,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 304,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 64,
  },
  {
    label: 'Claritromicina ',
    value: 'Claritromicina ',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 500 mg q 12h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 500 mg q 12h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 250 mg q 8h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 250 mg q 12h',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose pós diálise',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose normal',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 66,
  },
  {
    label: 'Clindamicina',
    value: 'Clindamicina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'Não é necessário ajuste de dose em pacientes com insuficiência renal​.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
          {
            id: 203,
            text: 'Bula do profissional de Saúde. Clindamicina. EMS​ (aprovada pela Anvisa em 14/11/2023).',
          },
        ],
      },
    ],
    drugId: 67,
  },
  {
    label: 'Dabigatrana',
    value: 'Dabigatrana',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 50 mL/min: não é necessário ajuste da dose.',
          },
          {
            id: 102,
            text: 'ClCr 30-50 mL/min: não é necessário ajuste da dose, exceto em casos de prevenção de TEV em pacientes submetidos à cirurgia ortopédica de grande porte (150 mg uma vez ao dia).',
          },
          {
            id: 103,
            text: 'ClCr 15-30 mL/min: contraindicado (algumas referências consideram doses reduzidas, 150 mg/dia, na prevenção de cardioembolia em pacientes com FA).',
          },
          {
            id: 104,
            text: 'ClCr < 15 mL/min: contraindicado.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'U.S. Food & Drug Administration (FDA). FDA Label: Dabigatran. Camber Pharmaceuticals, Inc., Revised: 2/2025.',
          },
          {
            id: 202,
            text: 'Bula do profissional de saúde. PRADAXA®. Boehringer Ingelheim do Brasil Química e Farmacêutica Ltda, aprovada em 30/12/2024.',
          },
        ],
      },
    ],
    drugId: 78,
  },
  {
    label: 'Doxiciclina',
    value: 'Doxiciclina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'Estudos até o momento têm demonstrado que a administração de doxiciclina nas doses habitualmente recomendadas não leva a um acúmulo excessivo desse antibiótico em pacientes com insuficiência renal.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Bula do profissional de saúde. Doxiciclina. Sandoz do Brasil Ind. Farm. Ltda., aprovada pela Anvisa em 20/10/2023.',
          },
          {
            id: 202,
            text: 'U.S. Food & Drug Administration (FDA). FDA Label: Doxycycline. HF Acquisition Co LLC, DBA HealthFirst, Revised: 1/2024.',
          },
        ],
      },
    ],
    drugId: 102,
  },
  {
    label: 'Edoxabana',
    value: 'Edoxabana',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 50 mL/min: Nenhum ajuste de dose é necessário (60 mg q 24h).',
          },
          {
            id: 102,
            text: 'ClCr 15-50 mL/min: 30 mg q 24h.',
          },
          {
            id: 103,
            text: 'ClCr < 15 mL/min: contraindicado.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'U.S. Food & Drug Administration (FDA). FDA Label: edoxaban. Daiichi Sankyo Inc. Revised: 2/2024.',
          },
          {
            id: 202,
            text: 'Bula do profissional de saúde. LIXIANA®. Daiichi Sankyo Brasil Farmacêutica Ltda. 29/05/2024.',
          },
        ],
      },
    ],
    drugId: 105,
  },
  {
    label: 'Enoxaparina',
    value: 'Enoxaparina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 30 mL/min: 1 mg/kg q 12h (anticoagulação plena)',
          },
          {
            id: 102,
            text: 'ClCr < 30 mL/min: 1 mg/kg q 24h (anticoagulação plena)',
          },
        ],
      },
      {
        id: 2,
        title: 'Observação',
        items: [
          {
            id: 201,
            text: 'Pacientes com > 75 anos e ClCr > 30 mL/min devem ter a dose ajustada para 0,75 mg/kg q 12h.',
          },
          {
            id: 202,
            text: 'Pacientes com > 75 anos e ClCr < 30 mL/min devem ter a dose ajustada para 1 mg/kg q 24h.',
          },
        ],
      },
      {
        id: 3,
        title: 'Referências:',
        items: [
          {
            id: 301,
            text: 'Bula do profissional de saúde. HEPARINOX® (enoxaparina sódica). CRISTÁLIA - Produtos Químicos Farmacêuticos Ltda, 25/03/2021.',
          },
          {
            id: 302,
            text: 'V Diretriz da Sociedade Brasileira de Cardiologia sobre Tratamento do Infarto Agudo do Miocárdio com Supradesnível do Segmento ST, Volume 105, Nº 2, Suplemento 1, Agosto 2015.',
          },
        ],
      },
    ],
    drugId: 109,
  },
  {
    label: 'Eritromicina',
    value: 'Eritromicina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 10 mL/min: 500 mg q 6h',
          },
          {
            id: 102,
            text: 'ClCr < 10 mL/min: 500 mg q 12h',
          },
          {
            id: 103,
            text: 'Hemodiálise: Dose normal',
          },
          {
            id: 104,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose normal',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 111,
  },
  {
    label: 'Ertapenem',
    value: 'Ertapenem',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 1g q 24h',
          },
          {
            id: 102,
            text: 'ClCr 90-30 mL/min: 1g q 24h',
          },
          {
            id: 103,
            text: 'ClCr < 30 mL/min: 500 mg q 24h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 112,
  },
  {
    label: 'Etambutol',
    value: 'Etambutol',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 15 mg/kg q 24h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: sem ajuste',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 15 mg/kg q 24-36h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 15 mg/kg q 48h',
          },
          {
            id: 105,
            text: 'Hemodiálise: 15 mg/kg após procedimento',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: 15 mg/kg q 48h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Etionamida',
    value: 'Etionamida',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 750 mg/dia dividido q 8-12h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: sem ajuste',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: sem ajuste',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 50%',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Flucitosina',
    value: 'Flucitosina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'A flucitosina deve ser administrada com extremo cuidado a pacientes com função renal comprometida. Como a flucitosina é excretada principalmente pelos rins, a insuficiência renal pode levar ao acúmulo do medicamento.',
          },
          {
            id: 102,
            text: 'As concentrações séricas de flucitosina devem ser monitoradas para determinar a adequação da excreção renal em tais pacientes. Ajustes de dosagem devem ser feitos em pacientes com insuficiência renal para evitar o acúmulo progressivo do medicamento ativo.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'U.S. Food & Drug Administration (FDA). FDA Label: Flucytosine. American Health Packaging, Revised: 12/2024.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Fluconazol',
    value: 'Fluconazol',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 200-800 mg q 24h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 200-800 mg q 24h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 50%',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 50%',
          },
          {
            id: 105,
            text: 'Hemodiálise: dose após sessão',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr<10',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 132,
  },
  {
    label: 'Ganciclovir',
    value: 'Ganciclovir',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal - INDUÇÃO',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 5 mg/kg q 12h IV',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 5 mg/kg q 24h IV',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 1,25-2,5 mg/kg q 24h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 1,25 mg/kg 3x por semana',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose pós sessão',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr<10',
          },
        ],
      },
      {
        id: 2,
        title: 'Insuficiência Renal - MANUTENÇÃO',
        items: [
          {
            id: 201,
            text: 'ClCr > 90 mL/min: 5 mg/kg q 24h IV',
          },
          {
            id: 202,
            text: 'ClCr 90-50 mL/min: 2,5-5,0 mg/kg q 24h',
          },
          {
            id: 203,
            text: 'ClCr 50-10 mL/min: 0,6-1,25 mg/kg q 24h',
          },
          {
            id: 204,
            text: 'ClCr < 10 mL/min: 0,625 mg/kg 3x por semana',
          },
          {
            id: 205,
            text: 'Hemodiálise: 0,6 mg/kg pós sessão',
          },
          {
            id: 206,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr<10',
          },
        ],
      },
      {
        id: 3,
        title: 'Referências:',
        items: [
          {
            id: 301,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 302,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Gentamicina',
    value: 'Gentamicina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 80 mL/min: 5,1 mg/kg q 24h',
          },
          {
            id: 102,
            text: 'ClCr 60-80 mL/min: 4 mg/kg q 24h',
          },
          {
            id: 103,
            text: 'ClCr 40-60 mL/min: 3,5 mg/kg q 24h',
          },
          {
            id: 104,
            text: 'ClCr 30-40 mL/min: 2,5 mg/kg q 24h',
          },
          {
            id: 105,
            text: 'ClCr 20-30 mL/min: 4 mg/kg q 48h',
          },
          {
            id: 106,
            text: 'ClCr 10-20 mL/min: 3 mg/kg q 48h',
          },
          {
            id: 107,
            text: 'ClCr < 10 mL/min: 2 mg/kg q 72h e após diálise',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 142,
  },
  {
    label: 'Heparina não-fracionada',
    value: 'Heparina não-fracionada',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'Sem necessidade de ajuste de dose.',
          },
          {
            id: 102,
            text: 'Não requer reajustes durante a hemodiálise ou diálise peritoneal.',
          },
          {
            id: 103,
            text: 'Ajustar dose para manter TTPa entre 1,5 e 2.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Bula do profissional de saúde. Heparina. Cristália Prod. Quím. Farm. Ltda, aprovada pela Anvisa em 09/11/2017.',
          },
        ],
      },
    ],
    drugId: 151,
  },
  {
    label: 'Imipenem',
    value: 'Imipenem',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 500 mg q 6h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 250-500 mg q 6h a 8h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 250 mg q 6-12h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 125-250 mg q 12h',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose antes da diálise.',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr <10',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 159,
  },
  {
    label: 'Isoniazida',
    value: 'Isoniazida',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 500 mg q 6h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 250-500 mg q 6h a 8h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 250 mg q 6-12h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 125-250 mg q 12h',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose antes da diálise.',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr <10',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Itraconazol',
    value: 'Itraconazol',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 100-200 mg q 12h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 100-200 mg q 12h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 100-200 mg q 12h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 50%',
          },
          {
            id: 105,
            text: 'Hemodiálise: solução oral 100mg q 12-24h',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: solução oral 100mg q 12-24h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 170,
  },
  {
    label: 'Levofloxacino',
    value: 'Levofloxacino',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 500-750 mg VO/IV q 24h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 500-750 mg VO/IV q 24h',
          },
          {
            id: 103,
            text: 'ClCr 20-50 mL/min: 500-750 mg q 48h',
          },
          {
            id: 104,
            text: 'ClCr <20 mL/min: 500mg q 48h',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose para ClCr <20',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr <20',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 178,
  },
  {
    label: 'Linezolida',
    value: 'Linezolida',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 500-750 mg VO/IV q 24h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 500-750 mg VO/IV q 24h',
          },
          {
            id: 103,
            text: 'ClCr 20-50 mL/min: 500-750 mg q 48h',
          },
          {
            id: 104,
            text: 'ClCr <20 mL/min: 500mg q 48h',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose para ClCr <20',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr <20',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 182,
  },
  {
    label: 'Meropenem',
    value: 'Meropenem',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 1 g q 8h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 1 g q 8h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 1 g q 12h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 500 mg q 24h',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose antes da diálise.',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr <10',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 194,
  },
  {
    label: 'Metronidazol',
    value: 'Metronidazol',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 500 mg q 8h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 500 mg q 8h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 500 mg q 8h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 250 mg q 8h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 202,
  },
  {
    label: 'Micafungina',
    value: 'Micafungina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 100 mg q 24h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 100%',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 100%',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 100%',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Nitrofurantoína',
    value: 'Nitrofurantoína',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 50-100 mg/dia',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 50-100 mg/dia',
          },
          {
            id: 103,
            text: 'ClCr < 50 mL/min: Evitar',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 216,
  },
  {
    label: 'Oseltamivir',
    value: 'Oseltamivir',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 75 mg VO q 12h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 75 mg VO q 12h',
          },
          {
            id: 103,
            text: 'ClCr 30-50 mL/min: 75 mg q 12h',
          },
          {
            id: 104,
            text: 'ClCr <30 mL/min: 75 mg q 24h',
          },
          {
            id: 105,
            text: 'Hemodiálise: 30 mg em dias sem diálise',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: 30mg 1 vez por semana',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Penicilina G',
    value: 'Penicilina G',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 0,5-4 UM q 4h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 100%',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 75%',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 20-50%',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose antes da diálise.',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Mesma dose de ClCr<10.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Piperacilina-tazobactam',
    value: 'Piperacilina-tazobactam',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 4/0,5 g q 6-8h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 4/0,5 g q 6-8h',
          },
          {
            id: 103,
            text: 'ClCr 50-20 mL/min: 2/0,25 g q 6h',
          },
          {
            id: 104,
            text: 'ClCr < 20 mL/min: 2/0,25 g q 8h',
          },
          {
            id: 105,
            text: 'Terapia de Substituição Renal Contínua: 4,5 q 48h.',
          },
          {
            id: 106,
            text: 'Hemodiálise: Dose para ClCr<10 + 0,75 mg antes da diálise.',
          },
          {
            id: 107,
            text: 'Diálise Peritonial Ambulatorial Contínua: 4/0,25 g q 12h.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 240,
  },
  {
    label: 'Pirazinamida',
    value: 'Pirazinamida',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 25 mg/kg q 24h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: sem ajuste',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: sem ajuste',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 15-20 mg/kg q 24h',
          },
          {
            id: 105,
            text: 'Hemodiálise: 12 mg/kg/dose',
          },
          {
            id: 106,
            text: 'Diálise Peritonial: sem dados suficientes.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Polimixina B',
    value: 'Polimixina B',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 12.500 UI/kg q 12h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 7.500 UI/kg q 12h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 7.500 UI/kg q 12h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 7.500 UI/kg q 12h',
          },
          {
            id: 105,
            text: 'Hemodiálise: o ajuste é controverso.',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: o ajuste é controverso.',
          },
          {
            id: 107,
            text: 'Obs: Estudos de farmacocinética sugerem que a droga não é eliminada por via renal.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 243,
  },
  {
    label: 'Rifampicina',
    value: 'Rifampicina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 600 mg q 24h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: sem ajuste',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: sem ajuste',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: sem ajuste',
          },
          {
            id: 105,
            text: 'Hemodiálise: sem ajuste',
          },
          {
            id: 106,
            text: 'Diálise Peritonial: sem ajuste',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 261,
  },
  {
    label: 'Rivaroxabana',
    value: 'Rivaroxabana',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 50 mL/min: não é necessário ajuste da dose.',
          },
          {
            id: 102,
            text: 'ClCr 50-30 mL/min: não é necessário ajuste da dose, exceto na redução do risco de cardioembolia na Fibrilação Atrial Não Valvular (15 mg uma vez ao dia).',
          },
          {
            id: 103,
            text: 'ClCr 30-15 mL/min: uso não recomendado (algumas referências consideram o uso em dose normal para o tratamento de TEV, embora haja controvérsias nesta indicação).',
          },
          {
            id: 104,
            text: 'ClCr < 15 mL/min: contraindicado.',
          },
          {
            id: 105,
            text: 'Diálise: devido à alta ligação da rivaroxabana às proteínas plasmáticas, não se espera que esta seja dialisável. Uso não recomendado.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'U.S. Food & Drug Administration (FDA). FDA Label: Xarelto. Janssen Pharmaceuticals, Inc., Revised: 10/2024.',
          },
          {
            id: 202,
            text: 'Bula do profissional da saúde. Xarelto®. Bayer S.A., aprovada pela Anvisa em 27/01/2025.',
          },
        ],
      },
    ],
    drugId: 264,
  },
  {
    label: 'Sulfametoxazol-trimetoprima',
    value: 'Sulfametoxazol-trimetoprima',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 5-20 mg/kg/dia dividido q 6-12h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 5-20 mg/kg/dia dividido q 6-12h',
          },
          {
            id: 103,
            text: 'ClCr 30-50 mL/min: 5-7,5 mg/kg/dia q 8h',
          },
          {
            id: 104,
            text: 'ClCr 10-29 mL/min: 5-10 mg/kg/dia q 12h',
          },
          {
            id: 105,
            text: 'ClCr < 10 mL/min: não recomendado (se utilizado: 5-10 mg/kg/dia q 24h)',
          },
          {
            id: 106,
            text: 'Hemodiálise: não recomendado (se utilizado: 5-10 mg/kg/dia q 24h)',
          },
          {
            id: 107,
            text: 'Diálise Peritonial Ambulatorial Contínua: não recomendado (se utilizado: 5-10 mg/kg/dia q 24h)',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 283,
  },
  {
    label: 'Teicoplanina',
    value: 'Teicoplanina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 6 mg/kg/dia',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 6 mg/kg q 24h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 6 mg/kg q 48h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 6 mg/kg q 72h',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose para ClCr<10',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr<10',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 290,
  },
  {
    label: 'Tetraciclina',
    value: 'Tetraciclina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 500mg q 6-12h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 500mg q 8-12h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 500mg q 12-24h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 500mg q 24h',
          },
          {
            id: 105,
            text: 'Hemodiálise: são removidas lentamente por hemodiálise.',
          },
          {
            id: 106,
            text: 'Diálise Peritonial: não remove tetraciclinas efetivamente.',
          },
        ],
      },
      {
        id: 2,
        title: 'Observações',
        items: [
          {
            id: 201,
            text: 'O cloridrato de tetraciclina é excretado de forma inalterada, aproximadamente 70% por via renal; além desta, existe a eliminação fecal, via biliar e gastrintestinal, ou por baixa absorção de tetraciclina no intestino.',
          },
        ],
      },
      {
        id: 3,
        title: 'Referências:',
        items: [
          {
            id: 301,
            text: 'Aronoff GR, Bennett WM, Berns JS, et al. Drug Prescribing in Renal Failure: Dosing Guidelines for Adults and Children. 5th ed. American College of Physicians; 2007.',
          },
          {
            id: 302,
            text: 'Bula do profissional de saúde. Tetraciclina. LABORATÓRIO TEUTO BRASILEIRO S/A., aprovada em 24/01/2025.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Tigeciclina',
    value: 'Tigeciclina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'Não é necessário ajustar a dose da tigeciclina em pacientes com insuficiência renal ou nos submetidos a hemodiálise.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
          {
            id: 203,
            text: 'Bula do profissional de saúde. Tigeciclina.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Tobramicina',
    value: 'Tobramicina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 80 mL/min: 5,1 mg/kg q 24h',
          },
          {
            id: 102,
            text: 'ClCr 60-80 mL/min: 4 mg/kg q 24h',
          },
          {
            id: 103,
            text: 'ClCr 40-60 mL/min: 3,5 mg/kg q 24h',
          },
          {
            id: 104,
            text: 'ClCr 30-40 mL/min: 2,5 mg/kg q 24h',
          },
          {
            id: 105,
            text: 'ClCr 20-30 mL/min: 4 mg/kg q 48h',
          },
          {
            id: 106,
            text: 'ClCr 10-20 mL/min: 3 mg/kg q 48h',
          },
          {
            id: 107,
            text: 'ClCr < 10 mL/min: 2 mg/kg q 72h e após diálise',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 297,
  },
  {
    label: 'Valganciclovir',
    value: 'Valganciclovir',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 900 mg q 12h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 900 mg q 12h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 450 mg q 24h a 450 mg em dias alternados',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: null,
  },
  {
    label: 'Vancomicina',
    value: 'Vancomicina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 90 mL/min: 15-20 mg/kg q 12h (max 2g/dose) ou 1g q 12h',
          },
          {
            id: 102,
            text: 'ClCr 90-50 mL/min: 1g q 12h',
          },
          {
            id: 103,
            text: 'ClCr 50-10 mL/min: 1 g q 24-96h',
          },
          {
            id: 104,
            text: 'ClCr < 10 mL/min: 1 g q 4-7 dias',
          },
          {
            id: 105,
            text: 'Hemodiálise: Dose para ClCr<10',
          },
          {
            id: 106,
            text: 'Diálise Peritonial Ambulatorial Contínua: Dose para ClCr<10',
          },
          {
            id: 107,
            text: 'Terapia de Substituição Renal Contínua: 1 g q 24-96h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 302,
  },
  {
    label: 'Varfarina',
    value: 'Varfarina',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'Nenhum ajuste de dosagem específico é sugerido para pacientes com insuficiência renal.',
          },
          {
            id: 102,
            text: 'Monitore o INR com mais frequência em pacientes com função renal comprometida para manter o INR dentro da faixa terapêutica.',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'U.S. Food & Drug Administration (FDA). FDA Label: Warfarin. Coupler LLC, Revised: 2/2025.',
          },
        ],
      },
    ],
    drugId: 304,
  },
  {
    label: 'Voriconazol',
    value: 'Voriconazol',
    info: [
      {
        id: 1,
        title: 'Insuficiência Renal',
        items: [
          {
            id: 101,
            text: 'ClCr > 50 mL/min: Ataque de 6 mg/kg IV q 12h por 2 doses e depois 4 mg/kg/dia q 12h.',
          },
          {
            id: 102,
            text: 'ClCr < 50 mL/min: não utilizar formulação IV (ciclodextrina).',
          },
          {
            id: 103,
            text: 'Terapia de Substituição Renal Contínua: 4 mg/kg po q 12h',
          },
        ],
      },
      {
        id: 2,
        title: 'Referências:',
        items: [
          {
            id: 201,
            text: 'Santa Casa de São Carlos / Serviço de Controle de Infecção Relacionada a Assistência em Saúde (SCIRAS). Tabela da doses dos antimicrobianos na insuficiência renal, 2015-2017.',
          },
          {
            id: 202,
            text: 'Guia de Utilização de Anti-Infecciosos e Recomendações para a Prevenção de Infecções Relacionadas a Assistência à Saúde - 2015-2017 - HC-FMUSP.',
          },
        ],
      },
    ],
    drugId: 309,
  },
];

export const CorrecaoDoseFuncaoRenalProvider = ({
  children,
}: PropsWithChildren) => {
  const [age, setAge] = useState<number>(0);
  const [cockcroft, setCockcroft] = useState<number>(0);
  const [creatinine, setCreatinine] = useState<number>(0);
  const [drug, setDrug] = useState<string>('');
  const [drugId, setDrugId] = useState<number | null>(null);
  const [drugInfo, setDrugInfo] = useState<IDrugInfo[]>([]);
  const [gender, setGender] = useState<EGender>(EGender.Male);
  const [weight, setWeight] = useState<number>(0);

  const handleAge = useCallback((value: number): void => {
    setAge(value);
  }, []);

  const handleDrug = useCallback((value: string): void => {
    const selectedDrug = drugs.find((item) => item.value === value);
    setDrug(value);
    setDrugInfo(selectedDrug!.info);
    setDrugId(selectedDrug!.drugId);
  }, []);

  const handleGender = useCallback((value: EGender): void => {
    setGender(value);
  }, []);

  const handleCreatinine = useCallback((value: number): void => {
    setCreatinine(value);
  }, []);

  const handleWeight = useCallback((value: number): void => {
    setWeight(value);
  }, []);

  const calculateCockcroft = useCallback((): void => {
    // FÓRMULA:
    // SE MASCULINO: (140 - IDADE) * PESO / (72 * CREAT.)
    // SE FEMININO: ((140 - IDADE) * PESO / (72 * CREAT.)) * 0,85
    const ageParsed = new BigNumber(age);
    const creatinineParsed = new BigNumber(creatinine);
    const weightParsed = new BigNumber(weight);
    if (gender === EGender.Male) {
      const step1 = new BigNumber(72).multipliedBy(creatinineParsed);
      const step2 = new BigNumber(140).minus(ageParsed);
      const step3 = weightParsed.dividedBy(step1);
      const result = step2.multipliedBy(step3);
      setCockcroft(result.toNumber() || 0);
    }
    if (gender === EGender.Female) {
      const step1 = new BigNumber(72).multipliedBy(creatinineParsed);
      const step2 = new BigNumber(140).minus(ageParsed);
      const step3 = weightParsed.dividedBy(step1);
      const step4 = step2.multipliedBy(step3);
      const result = step4.multipliedBy(0.85);
      setCockcroft(result.toNumber() || 0);
    }
  }, [age, creatinine, gender, weight]);

  useEffect(() => {
    calculateCockcroft();
  }, [calculateCockcroft]);

  return (
    <CorrecaoDoseFuncaoRenalContext.Provider
      value={{
        age,
        cockcroft,
        creatinine,
        drug,
        drugId,
        drugInfo,
        gender,
        setAge: handleAge,
        setCreatinine: handleCreatinine,
        setDrug: handleDrug,
        setGender: handleGender,
        setWeight: handleWeight,
        weight,
      }}>
      {children}
    </CorrecaoDoseFuncaoRenalContext.Provider>
  );
};
